// Búsqueda semántica QRUZH (PROC-186) — Worker propio, aislado del tema.
// Medido 2026-08-31: 3 de cada 4 consultas en lenguaje natural devuelven CERO con el
// motor léxico de Shopify. Costo: 7.5 tokens por consulta (100k búsquedas ≈ US$0.015).
// Mismo origen desde el 31-ago (la ruta propia ya vive): sin CORS y un salto menos.
const QZ_BUSCAR_URL = '/api/buscar';

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // 'tag' se agrego el 31-ago: LOGOS siembra los codigos de proveedor como ETIQUETAS
    // (qz_modelo_tokens, 246 -> cab246) y este desplegable no las miraba, asi que su trabajo
    // funcionaba en /search y aqui devolvia 0. Medido en produccion antes y despues.
    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,variants.barcode,tag&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();

    // Capa semantica (PROC-186). Se SUMA al buscador de Shopify, no lo reemplaza: el motor
    // lexico + los tokens de modelo (246 -> cab246) siguen mandando para codigo y SKU.
    // Solo entra cuando el lexico trae poco o nada — que es el 3 de cada 4 medido el 31-ago:
    // "algo para escuchar musica sin cables" y "que le regalo a un nino" devuelven CERO.
    this.completarConSemantica(this.searchTerm);
  }

  // El buscador agrupa por (diseno, telefono): una tarjeta representa a todos los colores
  // de esa misma funda. Dos consecuencias que la vitrina TIENE que decir en voz alta:
  //   1. si los colores no valen lo mismo (medido: 10 grupos), un precio unico seria mentira
  //      -> "desde $X";
  //   2. si hay mas de uno, decirlo vende: "4 colores" es catalogo, no ruido.
  precioTexto(x) {
    const mx = (n) => '$' + Number(n).toLocaleString('es-MX');
    const max = Number(x.precio_max ?? x.precio);
    return max > Number(x.precio) ? `desde ${mx(x.precio)}` : mx(x.precio);
  }

  coloresTexto(x) {
    const n = Number(x.colores || 1);
    // estilo en linea: qz-sem__colores no tiene CSS en el tema y sin esto la nota
    // competiria visualmente con el precio, que es lo que el cliente vino a leer.
    return n > 1
      ? ` <span class="qz-sem__colores" style="opacity:.65;font-weight:400">· ${n} colores</span>`
      : '';
  }

  // Endpoint propio (Worker aislado). Si no responde, esta funcion no hace NADA y el
  // buscador queda exactamente como estaba: una busqueda semantica caida jamas puede
  // dejar la tienda sin buscador.
  completarConSemantica(searchTerm) {
    const termino = (searchTerm || '').trim();
    if (termino.length < 3) return;                       // tecleando, no buscando
    if (this.predictiveSearchResults.querySelector('[data-qz-semantica]')) return;

    const yaMostrados = new Set(
      [...this.predictiveSearchResults.querySelectorAll('a[href*="/products/"]')].map((a) =>
        (a.getAttribute('href') || '').split('?')[0].split('/products/').pop()
      )
    );

    // Si el lexico ya resolvio bien, no se estorba ni se gasta un embedding.
    if (yaMostrados.size >= 4) return;

    fetch(`${QZ_BUSCAR_URL}?q=${encodeURIComponent(termino)}&n=6`, { signal: this.abortController.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || !d.ok || !d.resultados || !d.resultados.length) return;
        if (this.searchTerm.trim() !== termino) return;   // el usuario ya escribe otra cosa

        const nuevos = d.resultados
          .filter((x) => !yaMostrados.has((x.url || '').split('/products/').pop()))
          .slice(0, 4);
        if (!nuevos.length) return;

        const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        const filas = nuevos
          .map(
            (x) => `
            <li class="predictive-search__list-item" role="option">
              <a href="${x.url}" class="predictive-search__item link link--text" tabindex="-1">
                ${x.imagen ? `<img src="${x.imagen}" alt="" width="50" height="50" loading="lazy" class="predictive-search__image">` : ''}
                <div class="predictive-search__item-content">
                  <p class="predictive-search__item-heading h5">${esc(x.producto)}</p>
                  <p class="qz-sem__precio">${this.precioTexto(x)}${this.coloresTexto(x)}</p>
                </div>
              </a>
            </li>`
          )
          .join('');

        const bloque = document.createElement('div');
        bloque.setAttribute('data-qz-semantica', '');
        bloque.innerHTML = `
          <h2 class="predictive-search__heading caption-with-letter-spacing" style="padding:0.8rem 1.6rem 0">
            Quizá buscabas
          </h2>
          <ul class="list-unstyled" role="listbox">${filas}</ul>`;

        const destino =
          this.predictiveSearchResults.querySelector('#predictive-search-results-groups-wrapper') ||
          this.predictiveSearchResults;
        destino.appendChild(bloque);

        // El mensaje "No se encontraron resultados" de Shopify NO se ve: medido en produccion
        // el 31-ago, sus dos nodos son span.hidden (0x0) y span.predictive-search-status
        // .visually-hidden (1x1, clip rect 0). Es decir: el cliente solo ve "Quiza buscabas".
        // Ocultarlo seria codigo muerto y, peor, arriesgaria tapar el contenedor con nuestros
        // propios resultados dentro. Lo que SI queda mal es la accesibilidad: el lector de
        // pantalla anuncia "no hay resultados" con cuatro productos en la lista. Se corrige el
        // texto, que es el defecto real.
        const estado = this.querySelector('.predictive-search-status');
        if (estado) {
          estado.textContent = `${nuevos.length} sugerencia${nuevos.length === 1 ? '' : 's'} para ${termino}`;
        }

        this.setLiveRegionResults();
      })
      .catch(() => {
        /* falla abierto a proposito: el buscador de Shopify ya respondio */
      });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
